// Thin fetch wrapper. Attaches the access token and transparently refreshes it
// once on a 401 before failing.

import { isRunningInAgent, pageNamesItsMarket, SALES_MARKET_HEADER } from '@rth/shared';
import { disconnectSocket } from '../lib/socket';

const ACCESS_KEY = 'rth.access';
const REFRESH_KEY = 'rth.refresh'; // legacy — refresh token now lives in an httpOnly cookie

/** localStorage keys that belong to the signed-in person rather than to the browser. Cleared on
 *  sign-out. Prefix-matched so a new per-user key does not have to be registered here. */
const PER_USER_PREFIX = 'rth.u.';

function clearPerUserScratch(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(PER_USER_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    /* private mode / storage disabled — nothing to clear */
  }
}

/** Namespace a scratch key to one user: `rth.u.<userId>.<name>`. Never share a bare key across
 *  users on a machine two people sign into. */
export function perUserKey(userId: string | null | undefined, name: string): string {
  return `${PER_USER_PREFIX}${userId ?? 'anon'}.${name}`;
}

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  // The refresh token is no longer kept in JS-readable storage — the server sets it as an
  // httpOnly cookie that the browser attaches to /api/auth calls automatically. Only the
  // short-lived access token stays here (in memory-ish localStorage) so a reload can re-auth.
  set(access: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.removeItem(REFRESH_KEY); // scrub any legacy refresh token
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    // Anything keyed to the person who was signed in goes with the session. RT Hub runs on shared
    // workstations — VAs swap seats — so a draft left in localStorage after sign-out is readable
    // by the next person at that machine, and drafts routinely name a client or a case.
    clearPerUserScratch();
  },
};

/**
 * Which Sales CRM (Australia / USA) this tab is showing, sent as `x-sales-market` on every call.
 * Held in memory only: lib/salesMarket.ts sets it once the signed-in person is known, and ONLY for
 * someone in more than one market — see that file for why a stale value must never be sent.
 */
export const salesMarketStore = {
  active: null as string | null,
  set(market: string | null) {
    this.active = market;
  },
};

function withSalesMarket(headers: Headers): Headers {
  // A call that names its own market keeps it — the Australia-vs-USA page asks for each in turn.
  if (headers.has(SALES_MARKET_HEADER)) return headers;
  // ⛔ Only a page inside an area that is separate per market names one. A shared area (RT Hub,
  // Payroll) is one copy for both, and an area not split yet still holds every market's records —
  // from either, the server fences to ALL the person's markets. See pageNamesItsMarket.
  if (salesMarketStore.active && pageNamesItsMarket(window.location.pathname)) headers.set(SALES_MARKET_HEADER, salesMarketStore.active);
  return headers;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message);
  }
}

// Single-flight refresh: when several requests 401 at once (e.g. a page firing
// multiple queries as the access token expires), they all await ONE
// /api/auth/refresh instead of each starting their own. Concurrent refreshes would
// race on the rotating refresh token and 401 each other, storming the endpoint
// (and tripping its rate limit). At most one refresh is in flight per tab.
let refreshInFlight: Promise<RefreshResult> | null = null;

// ⛔ Once the server has DEFINITELY rejected the refresh token the session is over, and every
// later 401 used to start a brand-new POST /api/auth/refresh: a page with a dozen polling queries
// re-storms an endpoint that is rate-limited on purpose, while the authenticated shell stays on
// screen with every widget quietly failing and no login prompt anywhere. Latch it instead — the
// first definite rejection clears the session and sends the tab to /login, once.
let sessionEnded = false;

/** 'transient' is not a logout: a 429 from the refresh limiter or a 5xx must keep the session, or
 *  a blip becomes "randomly logged out mid-shift" (same rule as AuthContext's bootstrap). */
type RefreshResult = 'ok' | 'expired' | 'transient';

async function refreshAccess(): Promise<boolean> {
  if (sessionEnded) return false;
  if (!refreshInFlight) {
    refreshInFlight = doRefresh();
    // Free the slot once it settles so a later 401 can refresh again.
    void refreshInFlight.finally(() => {
      refreshInFlight = null;
    });
  }
  const result = await refreshInFlight;
  if (result === 'expired') endSession();
  return result === 'ok';
}

/** Routes that render without a session (the `!user` branch of App.tsx). ⛔ A dead session must not
 *  bounce someone off one of these: the person signing a contract, sitting a timed assessment or
 *  approving overtime from an email link belongs on that page, whatever stale token their browser
 *  happens to be carrying. Their session is still cleared — they just aren't dragged to /login. */
const PUBLIC_PREFIXES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  '/ot-approve',
  '/sign/',
  '/view/',
  '/interview-day/',
  '/apply/',
  '/book',
  '/assess/',
  '/onboard/',
  '/oauth/consent',
  '/privacy',
];

/** The refresh token is gone or revoked — nothing this tab does from here can succeed. Tear the
 *  session down and send the user to the login page exactly once, however many calls 401 at once. */
function endSession(): void {
  if (sessionEnded) return;
  sessionEnded = true;
  tokenStore.clear();
  disconnectSocket(); // otherwise it keeps retrying the handshake through the navigation
  const path = window.location.pathname;
  if (!PUBLIC_PREFIXES.some((p) => path === p || path.startsWith(p))) window.location.assign('/login');
}

/** Refresh the access token (single-flight). Used by the socket manager to recover an expired
 *  session on reconnect without the user logging out/in. Returns true if a fresh token was stored. */
export function refreshAccessToken(): Promise<boolean> {
  return refreshAccess();
}

// ⛔ A page loaded inside the RT Hub Agent's embedded Messages window or the standalone RT
// Messages desktop app (apps/messages-desktop) NEVER has this httpOnly cookie: both sign in from
// their own Electron main process (a plain fetch, no renderer cookie jar) and bridge the access
// token through localStorage/contextBridge instead, refreshing it themselves on their own schedule
// with the real refresh token — see apps/messages-desktop/README.md's session-bridge section. So
// this call was guaranteed to 401 in both, every time, and `doRefresh` mistook "no cookie in THIS
// window" for "logged out": `endSession()` wiped the bridged token and navigated the window to the
// web app's /login page — which has no sign-in form wired to it there (sign-in is a separate native
// window) — killing a perfectly good session and whatever the person was mid-typing. Same
// Electron-detection apps/web/src/lib/agent.ts already uses for its own (unrelated) purpose.
const NO_REFRESH_COOKIE =
  typeof window !== 'undefined' && isRunningInAgent({ userAgent: window.navigator?.userAgent, preloadFlag: window.rthAgent?.isAgent });

async function doRefresh(): Promise<RefreshResult> {
  // Bridged session: the token in localStorage is kept fresh by the host app's own refresh loop,
  // not by this call. Never attempt it (it cannot succeed) and never treat its absence as expiry.
  if (NO_REFRESH_COOKIE) return 'transient';
  // The refresh token rides in the httpOnly cookie (sent automatically, same-origin). Bridge:
  // a session created before this change still has a token in localStorage and NO cookie yet —
  // send it in the body once so the server can re-home it into the cookie; it's scrubbed on set().
  const legacy = localStorage.getItem(REFRESH_KEY);
  const res = await fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'same-origin',
    ...(legacy ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refreshToken: legacy }) } : {}),
  });
  if (!res.ok) return res.status === 401 || res.status === 403 ? 'expired' : 'transient';
  const data = (await res.json()) as { accessToken: string };
  tokenStore.set(data.accessToken);
  return 'ok';
}

/** Fetch with the access token, transparently refreshing ONCE on a 401 (mirrors `api()`). The file
 *  helpers below all move binaries the `api()` JSON wrapper can't, but they must still recover an
 *  expired 15-min access token — otherwise a stale token turns every attachment / document open,
 *  upload and download into a failure even though the session is perfectly valid. ⛔ Every one of
 *  them goes through here: a hand-built `Authorization` header on a bare `fetch` is exactly the
 *  bug this exists to prevent, and one such upload silently dropped a shift's screenshots. */
export async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const withAuth = (): RequestInit => {
    const headers = withSalesMarket(new Headers(init?.headers));
    if (tokenStore.access) headers.set('Authorization', `Bearer ${tokenStore.access}`);
    return { ...init, headers };
  };
  const res = await fetch(path, withAuth());
  if (res.status === 401 && (await refreshAccess())) return fetch(path, withAuth());
  return res;
}

/** Read the API's `{ error: { message } }` envelope off a failed response, falling back when the
 *  body isn't ours (a proxy's HTML 502, an empty 401). */
async function errorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { message?: string } };
    return body.error?.message ?? fallback;
  } catch {
    return fallback;
  }
}

// Opening an authed file means fetching bytes first, THEN showing them — and that ordering is a
// popup-blocker trap. `window.open()` only escapes the blocker when it is called synchronously
// inside the user's click; awaiting the fetch/blob first pushes the open past the gesture window,
// so the tab is silently blocked. The bigger the file the more reliably it happens, which is why
// PNG screenshots (megabytes) wouldn't open on a ticket while small text PDFs did — and because a
// blocked open just returns null (no throw), the caller's `.catch` never fires and the user sees
// nothing at all. Fix: grab the tab up front, within the gesture, and only navigate it once the
// bytes are ready; if it was blocked anyway, fall back to a programmatic anchor click.

/** Open a new tab synchronously (call inside the click), to be navigated once bytes are ready. */
function openPendingTab(): Window | null {
  // No 'noopener' here: that makes window.open return null, and we need the handle to navigate it.
  // The destination is our own same-origin blob, not untrusted content, so we null the opener
  // ourselves for the same protection.
  const win = window.open('about:blank', '_blank');
  if (win) win.opener = null;
  return win;
}

/** Show a fetched blob in `win` (from openPendingTab), or via an anchor if the popup was blocked. */
function showBlobInTab(win: Window | null, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  if (win && !win.closed) {
    win.location.replace(url);
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Fetch a user document (with auth) and open it in a new browser tab. */
export async function openDocument(id: string): Promise<void> {
  const win = openPendingTab();
  try {
    const res = await authedFetch(`/api/documents/${id}/download`);
    if (!res.ok) throw new ApiError(res.status, 'Could not open document');
    showBlobInTab(win, await res.blob());
  } catch (err) {
    if (win && !win.closed) win.close();
    throw err;
  }
}

/** Open an authed binary file inline in a new tab (e.g. a candidate resume). */
export async function openAuthedFile(path: string): Promise<void> {
  const win = openPendingTab();
  try {
    const res = await authedFetch(path);
    if (!res.ok) throw new ApiError(res.status, 'Could not open file');
    showBlobInTab(win, await res.blob());
  } catch (err) {
    if (win && !win.closed) win.close();
    throw err;
  }
}

/** Fetch an authed binary file and save it straight away — no viewer tab. Unlike openAuthedFile,
 *  this never opens a new tab at all: the `download` attribute on a same-origin blob URL makes the
 *  browser save the file directly.
 *
 *  Returns the response headers so a caller can read what the server said ALONGSIDE the file — the
 *  ticket export uses it for `X-Ticket-Export-Truncated`. Existing callers ignore the return value;
 *  the server must still expose any such header via Access-Control-Expose-Headers. */
export async function downloadAuthedFile(path: string, filename: string): Promise<Headers> {
  const res = await authedFetch(path);
  if (!res.ok) throw new ApiError(res.status, 'Could not download file');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return res.headers;
}

/** POST a JSON body and open the binary response (e.g. a rendered PDF) in a new tab. */
export async function openAuthedFilePost(path: string, body: unknown): Promise<void> {
  const win = openPendingTab();
  try {
    const res = await authedFetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!res.ok) {
      let message = 'Could not render file';
      try {
        const b = (await res.json()) as { error?: { message?: string } };
        message = b.error?.message ?? message;
      } catch {
        /* non-JSON */
      }
      throw new ApiError(res.status, message);
    }
    showBlobInTab(win, await res.blob());
  } catch (err) {
    if (win && !win.closed) win.close();
    throw err;
  }
}

/** POST a JSON body and return the binary response as a Blob, for showing inline (an <object> or
 *  <iframe> off `URL.createObjectURL`) rather than in a new tab. Refreshes once on a 401 like
 *  `api()`; a non-OK response throws the server's own message. */
export async function fetchAuthedBlobPost(path: string, body: unknown): Promise<Blob> {
  const res = await authedFetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) {
    let message = 'Could not render file';
    try {
      const b = (await res.json()) as { error?: { message?: string } };
      message = b.error?.message ?? message;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(res.status, message);
  }
  return res.blob();
}

/** Upload a Telnyx call's browser-captured recording (raw bytes) — see
 *  TelnyxWebrtcDialerProvider.tsx's own header and apps/api's telnyxCallRecording.service.ts for the
 *  full story. Best-effort by design: a failed upload just means no AI summary for this one call,
 *  never a broken dialer, so every call site of this catches and swallows rather than surfacing an
 *  error to the rep mid-wrap-up. */
export async function uploadTelnyxCallRecording(callId: string, blob: Blob): Promise<void> {
  const headers = new Headers();
  headers.set('Content-Type', blob.type || 'audio/webm');
  const res = await authedFetch(`/api/integrations/twilio-voice/calls/${callId}/recording`, { method: 'POST', headers, body: blob });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const b = (await res.json()) as { error?: { message?: string } };
      message = b.error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
}

/** Upload a candidate's resume (raw bytes). */
export async function uploadCandidateResume(candidateId: string, file: File): Promise<void> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const qs = new URLSearchParams({ filename: file.name });
  const res = await authedFetch(`/api/recruiting/candidates/${candidateId}/resume?${qs.toString()}`, { method: 'POST', headers, body: file });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const b = (await res.json()) as { error?: { message?: string } };
      message = b.error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
}

/** Upload a note attachment for a sales lead (raw bytes). Returns an authed link to embed in the note. */
export async function uploadLeadAttachment(
  leadId: string,
  file: File,
): Promise<{ url: string; name: string; isImage: boolean }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const qs = new URLSearchParams({ filename: file.name });
  const res = await authedFetch(`/api/sales/leads/${leadId}/attachments?${qs.toString()}`, { method: 'POST', headers, body: file });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const b = (await res.json()) as { error?: { message?: string } };
      message = b.error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as { url: string; name: string; isImage: boolean };
}

/** Upload a note attachment for a partnership/client (raw bytes). Returns an authed link to embed. */
export async function uploadPartnershipNoteAttachment(
  clientId: string,
  file: File,
): Promise<{ url: string; name: string; isImage: boolean }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const qs = new URLSearchParams({ filename: file.name });
  const res = await authedFetch(`/api/partnerships/${clientId}/notes/attachments?${qs.toString()}`, { method: 'POST', headers, body: file });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const b = (await res.json()) as { error?: { message?: string } };
      message = b.error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as { url: string; name: string; isImage: boolean };
}

/** Upload one contract file to a client's partnership card (contracts signed outside RT Hub). */
export async function uploadPartnershipContractFile(clientId: string, file: File, meta: { title?: string; signedOn?: string }): Promise<{ id: string }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const qs = new URLSearchParams({ name: file.name });
  if (meta.title) qs.set('title', meta.title);
  if (meta.signedOn) qs.set('signedOn', meta.signedOn);
  const res = await authedFetch(`/api/partnerships/${clientId}/contract-files?${qs.toString()}`, { method: 'POST', headers, body: file });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res, 'Could not upload that file'));
  return (await res.json()) as { id: string };
}

/** Download an authed binary file (e.g. a payslip PDF) and save it via the browser. */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const res = await authedFetch(path);
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res, 'Could not download file'));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Upload a file (raw bytes) to a VA's profile as a pay summary / contract. */
export async function uploadDocument(params: {
  userId: string;
  docType: 'PAY_SUMMARY' | 'CONTRACT';
  title: string;
  file: File;
  periodStart?: string;
  periodEnd?: string;
}): Promise<void> {
  const qs = new URLSearchParams({ userId: params.userId, docType: params.docType, title: params.title });
  if (params.periodStart) qs.set('periodStart', params.periodStart);
  if (params.periodEnd) qs.set('periodEnd', params.periodEnd);
  const headers = new Headers();
  headers.set('Content-Type', params.file.type || 'application/octet-stream');
  const res = await authedFetch(`/api/documents/upload?${qs.toString()}`, { method: 'POST', headers, body: params.file });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      message = body.error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
}

/** Upload the signed-in user's RT TV hype video (raw bytes). */
export async function uploadCelebrationVideo(file: File): Promise<{ hasVideo: boolean; sizeBytes?: number }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const res = await authedFetch('/api/celebrations/video', { method: 'POST', headers, body: file });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      message = body.error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json();
}

/** Upload the office-wide RT TV celebration sound (admin; raw bytes). */
export async function uploadCelebrationSound(file: File): Promise<{ exists: boolean }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const res = await authedFetch('/api/celebrations/sound', { method: 'POST', headers, body: file });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      message = body.error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json();
}

/**
 * Upload a contract template PDF (raw bytes) → returns the new template id.
 *
 * ⛔ `oneOff` is not cosmetic. It decides what the server creates and who may create it:
 *   true  — the caller's own single-send document, owned by them, never in the shared library.
 *           Any sales user, and they can keep editing it (that IS the one-off flow).
 *   false — a new SHARED library template. ADMIN ONLY since 17 Sep 2026; a non-admin gets a 403
 *           naming the one-off button instead.
 */
export async function uploadContractTemplate(name: string, file: Blob, oneOff = false, folder?: string): Promise<{ id: string; pageCount: number }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/pdf');
  const res = await authedFetch(`/api/contracts/templates/upload?name=${encodeURIComponent(name)}${oneOff ? '&oneOff=1' : ''}${folder ? `&folder=${encodeURIComponent(folder)}` : ''}`, {
    method: 'POST',
    headers,
    body: file,
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      message = body.error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json();
}

/** Insert another PDF's pages into an existing contract template (#498 item 23a) — appended at the
 *  end when `atIndex` is omitted, or landing at that exact position (0 = front) when given (#498
 *  item 23x, e.g. the "..." per-page menu's "Add PDF's here"). */
export async function addTemplatePages(
  templateId: string,
  file: File,
  atIndex?: number,
): Promise<{ pageCount: number; pageSizes: { w: number; h: number }[] }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/pdf');
  const qs = atIndex !== undefined ? `?atIndex=${atIndex}` : '';
  const res = await authedFetch(`/api/contracts/templates/${templateId}/pages${qs}`, { method: 'POST', headers, body: file });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      message = body.error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json();
}

/** Remove one page from a template — the "..." per-page menu's "Delete page" (#498 item 23x
 *  follow-up). Refused on a template's last remaining page. */
export function deleteTemplatePage(templateId: string, pageIndex: number): Promise<{ pageCount: number; pageSizes: { w: number; h: number }[]; removedFieldCount: number }> {
  return api(`/api/contracts/templates/${templateId}/pages/${pageIndex}`, { method: 'DELETE' });
}

/** Remove several pages at once — the Pages panel's multi-select bulk delete (#498 item 23x
 *  follow-up). Refused if the selection would remove every page. */
export function deleteTemplatePages(
  templateId: string,
  pageIndices: number[],
): Promise<{ pageCount: number; pageSizes: { w: number; h: number }[]; removedFieldCount: number; removedPageCount: number }> {
  return api(`/api/contracts/templates/${templateId}/pages`, { method: 'DELETE', body: JSON.stringify({ pageIndices }) });
}

/** #498 item 23z: find or create the calling user's personal draft of a shared template, so two
 *  BDMs composing off the same template never edit the same rows at once. Returns the draft's own
 *  id — navigate the builder there instead of the shared template's id. */
export function openTemplateDraft(templateId: string): Promise<{ id: string }> {
  return api(`/api/contracts/templates/${templateId}/my-draft`, { method: 'POST' });
}

/** #498 item 23y, manager-only: lock this template's current pages + fields as its "original" — every
 *  send from here on reverts the live template back to exactly this afterwards. */
export function lockTemplateOriginal(templateId: string): Promise<{ ok: true; pageCount: number; fieldCount: number }> {
  return api(`/api/contracts/templates/${templateId}/original-lock`, { method: 'POST' });
}

/** #498 item 23y, manager-only: undo a lock — the template goes back to never resetting on send. */
export function unlockTemplateOriginal(templateId: string): Promise<{ ok: true }> {
  return api(`/api/contracts/templates/${templateId}/original-lock`, { method: 'DELETE' });
}

/** Upload the signed-in user's profile picture (raw image bytes) → returns a fresh view URL. */
export async function uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const res = await authedFetch('/api/users/me/avatar', { method: 'POST', headers, body: file });
  if (!res.ok) {
    let message = res.statusText;
    try {
      message = ((await res.json()) as { error?: { message?: string } }).error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json();
}

/** Upload a custom chat sticker (raw image bytes) → returns the stored sticker. */
export async function uploadSticker(file: File): Promise<{ id: string; url: string; storageKey: string; contentType: string; bytes: number }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const res = await authedFetch('/api/messaging/stickers', { method: 'POST', headers, body: file });
  if (!res.ok) {
    let message = res.statusText;
    try {
      message = ((await res.json()) as { error?: { message?: string } }).error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json();
}

/** Upload a chat attachment (raw bytes) → returns the stored file descriptor. */
export async function uploadChatAttachment(file: File): Promise<{ storageKey: string; fileName: string; fileType: string; fileSize: number }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const res = await authedFetch(`/api/messaging/attachments/upload?name=${encodeURIComponent(file.name)}`, { method: 'POST', headers, body: file });
  if (!res.ok) {
    let message = res.statusText;
    try {
      message = ((await res.json()) as { error?: { message?: string } }).error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json();
}

/** Upload one document to a ticket, returning the attachment descriptor to attach to a message. */
export async function uploadTicketAttachment(file: File): Promise<{ storageKey: string; fileName: string; fileType: string; fileSize: number }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const res = await authedFetch(`/api/tickets/attachments/upload?name=${encodeURIComponent(file.name)}`, { method: 'POST', headers, body: file });
  if (!res.ok) {
    let message = res.statusText;
    try {
      message = ((await res.json()) as { error?: { message?: string } }).error?.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json();
}

/**
 * Upload an inline note/comment attachment for a ticket (paste / drag / attach in RichNoteEditor).
 * Reuses the SAME ticket-attachment pipeline as the Attach button, then returns an authed object URL
 * to embed in the note body. The server registers the referenced key as a TicketAttachment on save.
 */
export async function uploadTicketInlineAttachment(file: File): Promise<{ url: string; name: string; isImage: boolean }> {
  const up = await uploadTicketAttachment(file);
  return { url: `/api/tickets/attachments/object/${encodeURIComponent(up.storageKey)}`, name: up.fileName, isImage: (up.fileType || '').startsWith('image/') };
}

/** Deadline for one JSON call. Sits just above nginx's 60 s proxy_read_timeout on /api/, so it can
 *  never abort a request the proxy would still have answered — Ask RT Hub deliberately spends up
 *  to ~55 s in one call. It is there for the case nginx can't cover: a connection that dies with
 *  no response at all (the laptop slept, the phone changed network), where the promise otherwise
 *  never settles and the query sits `isPending` behind a spinner that never becomes an error.
 *  Pass a smaller `timeoutMs` on a call that should give up sooner; don't remove it. */
const DEFAULT_TIMEOUT_MS = 65_000;

export async function api<T = unknown>(
  path: string,
  options: RequestInit & { retry?: boolean; timeoutMs?: number } = {},
): Promise<T> {
  const { retry = true, timeoutMs = DEFAULT_TIMEOUT_MS, ...init } = options;
  const headers = withSalesMarket(new Headers(init.headers));
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');
  if (tokenStore.access) headers.set('Authorization', `Bearer ${tokenStore.access}`);

  // ⛔ A fresh controller per attempt. The 401 path below re-enters api(), and an already-armed
  // (or already-fired) controller would abort the second attempt before it got anywhere.
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(path, { ...init, headers, signal: ctrl.signal });

    if (res.status === 401 && retry && (await refreshAccess())) {
      return await api<T>(path, { ...options, retry: false });
    }

    if (!res.ok) {
      let message = res.statusText;
      let code: string | undefined;
      try {
        const body = (await res.json()) as { error?: { message?: string; code?: string } };
        message = body.error?.message ?? message;
        code = body.error?.code;
      } catch {
        /* non-JSON error */
      }
      // Company-wide MFA enforcement: the API blocks everything until the user enrols. Send them to
      // Settings to set it up (guarded so Settings' own non-exempt calls don't loop the redirect).
      if (code === 'mfa_enrollment_required' && !window.location.pathname.startsWith('/settings')) {
        window.location.assign('/settings?enroll=required');
      }
      // The market this tab was showing is no longer on the person's list (a director took it off
      // while they were working). Stop sending it — the server then puts them in a market that IS
      // theirs — and reload once from the front page, since everything on screen is the old market's.
      if (code === 'sales_market_forbidden' && salesMarketStore.active) {
        salesMarketStore.set(null);
        window.location.assign('/');
      }
      throw new ApiError(res.status, message, code);
    }

    if (res.status === 204) return undefined as T;
    const ct = res.headers.get('content-type') ?? '';
    return (ct.includes('application/json') ? await res.json() : await res.text()) as T;
  } catch (err) {
    // Surface the abort as an ApiError so every existing isError / onError path renders it the
    // way it renders any other failure, instead of leaking a bare DOMException.
    if (ctrl.signal.aborted && !(err instanceof ApiError)) {
      throw new ApiError(0, 'The request timed out — check your connection and try again.');
    }
    throw err;
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Upload one photo or PDF for a payroll dispute (Ara, 7 Oct 2026 — optional). Its id is then sent
 * with the dispute. The server sniffs the type from the bytes.
 */
export async function uploadDisputeAttachment(file: File): Promise<{ id: string; fileName: string; contentType: string; bytes: number }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const qs = new URLSearchParams({ name: file.name });
  const res = await authedFetch(`/api/payroll-disputes/attachments?${qs.toString()}`, { method: 'POST', headers, body: file });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res, 'Could not upload that file'));
  return ((await res.json()) as { attachment: { id: string; fileName: string; contentType: string; bytes: number } }).attachment;
}

/**
 * Upload one reimbursement receipt (raw bytes). Returns the stored receipt, whose id is then listed
 * in the claim's `receiptIds`. The server SNIFFS the type from the bytes and ignores the
 * Content-Type sent here — it is set only so a proxy in between does not choke on the body.
 */
/**
 * Upload one document to a person's Payroll document vault (raw body, like a receipt). The server
 * decides the file's type from its bytes and encrypts it before it is stored.
 */
export async function uploadPayrollDocument<T>(userId: string, file: File, meta: { kind: string; title?: string; expiresOn?: string }): Promise<T> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const qs = new URLSearchParams({ kind: meta.kind, name: file.name });
  if (meta.title?.trim()) qs.set('title', meta.title.trim());
  if (meta.expiresOn) qs.set('expiresOn', meta.expiresOn);
  const res = await authedFetch(`/api/payroll/people/${encodeURIComponent(userId)}/documents?${qs.toString()}`, { method: 'POST', headers, body: file });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res, 'Could not upload that document'));
  return ((await res.json()) as { document: T }).document;
}

export async function uploadReimbursementReceipt(file: File): Promise<{ id: string; fileName: string; contentType: string; bytes: number }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const qs = new URLSearchParams({ name: file.name });
  const res = await authedFetch(`/api/reimbursements/receipts?${qs.toString()}`, { method: 'POST', headers, body: file });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res, 'Could not upload that receipt'));
  return ((await res.json()) as { receipt: { id: string; fileName: string; contentType: string; bytes: number } }).receipt;
}

/** Upload an IMS Repository attachment (PDF/DOCX) or proof of approval (also PNG/JPG). Raw body; the server sniffs the type. */
export async function uploadImsFile(file: File, slot: 'attachment' | 'proof'): Promise<{ id: string; fileName: string }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  const qs = new URLSearchParams({ name: file.name, slot });
  const res = await authedFetch(`/api/ims/files?${qs.toString()}`, { method: 'POST', headers, body: file });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res, 'Could not upload that file'));
  return ((await res.json()) as { file: { id: string; fileName: string } }).file;
}
